import { createHttpService } from "../ApiService";
import {
  TCreateMaterialGroupRequest,
  TCreateMaterialItemPayload,
  TCreateMaterialResponse,
  TMaterialGroup,
  TMaterialItem,
  TMaterialListFilters,
  TMaterialListResponse,
  TUpdateMaterialGroupRequest,
  TUpdateMaterialItemPayload,
} from "./material.interfaces";
import { materialGroupListMock } from "../../__mocks__/material/materialMock";

const buildPaginatedMock = (
  filters: TMaterialListFilters | undefined,
): TMaterialListResponse => {
  const f = filters ?? {};
  const filtered = materialGroupListMock.filter((group) => {
    if (f.instituteId && group.instituteId !== f.instituteId) return false;
    if (f.category && group.category !== f.category) return false;
    if (f.status && group.status !== f.status) return false;
    if (f.department && group.department !== f.department) return false;
    if (f.location && group.location !== f.location) return false;
    if (f.search) {
      const q = f.search.toLowerCase();
      const haystack = [
        group.name,
        group.description ?? "",
        group.department ?? "",
        group.location ?? "",
        group.supplier ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const page = f.page ?? 1;
  const limit = f.limit ?? 20;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);
  return { data, count: filtered.length };
};

export const useMaterialService = () => {
  const httpAPI = createHttpService();
  const basePath = "/materials";

  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";
  const shouldUseMock = appWithMockData;

  const getMaterialGroups = (filters?: TMaterialListFilters) => {
    if (shouldUseMock) {
      return Promise.resolve(buildPaginatedMock(filters));
    }
    return httpAPI.academicsRoot.get<TMaterialListResponse>(basePath, {
      params: filters,
    });
  };

  const getMaterialGroupById = (id: string) => {
    if (shouldUseMock) {
      const group = materialGroupListMock.find((g) => g.id === id);
      if (!group) {
        return Promise.reject(new Error("Material group not found"));
      }
      return Promise.resolve(group);
    }
    // No dedicated GET-by-id in OpenAPI; fall back to filtering the list.
    return httpAPI.academicsRoot
      .get<TMaterialListResponse>(basePath)
      .then((res) => {
        const found = res.data.find((g) => g.id === id);
        if (!found) throw new Error("Material group not found");
        return found;
      });
  };

  const createMaterialGroup = (request: TCreateMaterialGroupRequest) => {
    if (shouldUseMock) {
      const now = new Date().toISOString();
      const group: TMaterialGroup = {
        id: `material-group-${Date.now()}`,
        instituteId: request.instituteId,
        name: request.name,
        description: request.description,
        category: request.category,
        status: request.status ?? "available",
        condition: request.condition ?? "good",
        location: request.location,
        quantity: request.quantity ?? 0,
        minimumQuantity: request.minimumQuantity,
        supplier: request.supplier,
        cost: request.cost,
        department: request.department,
        notes: request.notes,
        tags: request.tags,
        documents: { receipts: [], invoices: [], payments: [] },
        rollups: {
          totalCount: request.quantity ?? 0,
          availableCount: request.quantity ?? 0,
          inUseCount: 0,
          maintenanceCount: 0,
          retiredCount: 0,
          totalValue: (request.quantity ?? 0) * (request.cost ?? 0),
        },
        items: [],
        createdAt: now,
        updatedAt: now,
      };
      materialGroupListMock.push(group);
      return Promise.resolve(group);
    }
    // BE takes instituteId from JWT; do not send.
    const { instituteId: _instituteId, ...materialBody } = request;
    void _instituteId;
    return httpAPI.academicsRoot
      .post<TCreateMaterialResponse>(basePath, { material: materialBody })
      .then((res) => res.material);
  };

  const updateMaterialGroup = (
    materialGroupId: string,
    request: TUpdateMaterialGroupRequest,
  ) => {
    if (shouldUseMock) {
      const idx = materialGroupListMock.findIndex(
        (g) => g.id === materialGroupId,
      );
      if (idx === -1) {
        return Promise.reject(new Error("Material group not found"));
      }
      const next: TMaterialGroup = {
        ...materialGroupListMock[idx],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      materialGroupListMock[idx] = next;
      return Promise.resolve(next);
    }
    return httpAPI.academicsRoot
      .patch<TCreateMaterialResponse>(`${basePath}/${materialGroupId}`, {
        material: request,
      })
      .then((res) => res.material);
  };

  const addMaterialItem = (
    materialGroupId: string,
    payload: TCreateMaterialItemPayload,
  ) => {
    if (shouldUseMock) {
      const group = materialGroupListMock.find((g) => g.id === materialGroupId);
      if (!group) {
        return Promise.reject(new Error("Material group not found"));
      }
      const newItem: TMaterialItem = {
        ...payload.item,
        id: `material-item-${Date.now()}`,
      };
      group.items = [...(group.items ?? []), newItem];
      group.updatedAt = new Date().toISOString();
      return Promise.resolve(newItem);
    }
    return httpAPI.academicsRoot.post<TMaterialItem>(
      `${basePath}/${materialGroupId}/items`,
      payload,
    );
  };

  const updateMaterialItem = (
    materialGroupId: string,
    itemId: string,
    payload: TUpdateMaterialItemPayload,
  ) => {
    if (shouldUseMock) {
      const group = materialGroupListMock.find((g) => g.id === materialGroupId);
      if (!group) {
        return Promise.reject(new Error("Material group not found"));
      }
      const idx = (group.items ?? []).findIndex((it) => it.id === itemId);
      if (idx === -1) {
        return Promise.reject(new Error("Material item not found"));
      }
      const updated: TMaterialItem = {
        ...(group.items as TMaterialItem[])[idx],
        ...payload.item,
        id: itemId,
      };
      (group.items as TMaterialItem[])[idx] = updated;
      group.updatedAt = new Date().toISOString();
      return Promise.resolve(updated);
    }
    return httpAPI.academicsRoot.patch<TMaterialItem>(
      `${basePath}/${materialGroupId}/items/${itemId}`,
      payload,
    );
  };

  const deleteMaterialItem = (materialGroupId: string, itemId: string) => {
    if (shouldUseMock) {
      const group = materialGroupListMock.find((g) => g.id === materialGroupId);
      if (!group) {
        return Promise.reject(new Error("Material group not found"));
      }
      const before = group.items?.length ?? 0;
      group.items = (group.items ?? []).filter((it) => it.id !== itemId);
      const after = group.items.length;
      if (before === after) {
        return Promise.reject(new Error("Material item not found"));
      }
      group.updatedAt = new Date().toISOString();
      return Promise.resolve({ success: true });
    }
    return httpAPI.academicsRoot.delete<{ success: boolean }>(
      `${basePath}/${materialGroupId}/items/${itemId}`,
    );
  };

  return {
    getMaterialGroups,
    getMaterialGroupById,
    createMaterialGroup,
    updateMaterialGroup,
    addMaterialItem,
    updateMaterialItem,
    deleteMaterialItem,
  };
};
